import {Form, FormField, FormSubmitHandler, Label} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {z} from 'zod';

import {DeepPartial} from 'react-hook-form';
import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {
  useCreateBusinessCaseProformaInvoiceSettingMutation,
  useGetBusinessCaseProformaInvoiceSettingQuery,
  useUpdateBusinessCaseProformaInvoiceSettingMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {ApiError, NullishBoolean, useNavigate} from 'shared/web';

type FormValues = {
  branchId: string | null;
  shouldGenerate: boolean;
};

const breadcrumbs = [
  {
    label: i18n.t('page.accountingSettings.labels.proformaCreation'),
    href: settingsRoutes.ProformaCreationOverview,
  },
];

export function ProformaCreationDetail() {
  const {id} = useParams();
  const isCreating = isNil(id);
  const navigate = useNavigate();
  const [updateProformaCreation] = useUpdateBusinessCaseProformaInvoiceSettingMutation();
  const [createProformaCreation] = useCreateBusinessCaseProformaInvoiceSettingMutation();

  const {
    data: proformaCreation,
    isLoading: isProformaCreationLoading,
    isError: isProformaCreationError,
  } = useGetBusinessCaseProformaInvoiceSettingQuery({id: id ?? ''}, {skip: isCreating});

  const {
    data: branches,
    branchOptions,
    isLoading: isBranchesLoading,
    isError: isBranchesError,
  } = useBranches();

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const proformaSettings = data.shouldGenerate
      ? 'with-proforma-invoice'
      : 'without-proforma-invoice';

    if (isCreating) {
      if (isNil(data.branchId)) {
        return;
      }

      await createProformaCreation({
        branchId: data.branchId,
        proformaInvoiceSettings: proformaSettings,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.ProformaCreationOverview);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
      return;
    }
    await updateProformaCreation({
      id: id ?? '',
      branchId: data.branchId,
      proformaInvoiceSettings: proformaSettings,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.ProformaCreationOverview);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const isLoading = isProformaCreationLoading || isBranchesLoading;
  const isError = isProformaCreationError || isBranchesError;

  const branchName =
    branches?.branchListItems.find((branch) => branch.id === proformaCreation?.branchId)
      ?.marketingName ?? i18n.t('page.accounting.labels.tenantDefinition');

  const defaultValues: DeepPartial<FormValues> = {
    shouldGenerate: proformaCreation?.proformaInvoiceSettings === 'with-proforma-invoice',
    branchId: proformaCreation?.branchId ?? null,
  };

  return (
    <SettingsTemplate
      header={{
        title: branchName,
        breadcrumbs,
      }}
      data-testid={testIds.settings.proformaGeneration('page')}
      isLoading={isLoading}
      isError={isError}
    >
      <Form<FormValues>
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        experimentalZodSchema={getProformaCreationSchema(proformaCreation?.isSystem)}
        shouldWatchForUnsavedChanges
      >
        {(control, _formApi, navigateWithoutBlocker) => (
          <VStack spacing={4}>
            <SettingsSection>
              <Grid columns={2}>
                <FormField
                  control={control}
                  type="choice"
                  name="branchId"
                  isNotClearable
                  options={branchOptions}
                  isDisabled={proformaCreation?.isSystem}
                  label={i18n.t('entity.cashRegister.parameters.branch')}
                  data-testid={testIds.settings.ProformaCreationCreate('branch')}
                  placeholder={
                    proformaCreation?.isSystem
                      ? i18n.t('page.accounting.labels.tenantDefinition')
                      : undefined
                  }
                  isRequired={!proformaCreation?.isSystem}
                />
              </Grid>
            </SettingsSection>
            <VStack spacing={1}>
              <Label>{i18n.t('page.accountingSettings.labels.displayType')}</Label>
              <FormField
                control={control}
                type="checkbox"
                data-testid={testIds.settings.ProformaCreationCreate('shouldGenerate')}
                name="shouldGenerate"
                label={i18n.t('general.labels.yes')}
              />
            </VStack>
            <SettingsFooter
              actions={[
                {
                  type: 'button',
                  title: i18n.t('general.actions.discardChanges'),
                  onClick: () => navigateWithoutBlocker(settingsRoutes.ProformaCreationOverview),
                  variant: 'secondary',
                  'data-testid': testIds.settings.ProformaCreationCreate('discard'),
                },
                {
                  type: 'form-button',
                  control,
                  buttonType: 'submit',
                  title: i18n.t('general.actions.saveChanges'),
                  'data-testid': testIds.settings.ProformaCreationCreate('save'),
                },
              ]}
            />
          </VStack>
        )}
      </Form>
    </SettingsTemplate>
  );
}

const getProformaCreationSchema = (isSystem: NullishBoolean) => {
  const branchIdSchema = isTrue(isSystem)
    ? z.string().nullable()
    : z.string().nonempty({message: i18n.t('general.errors.mixed.required')});

  return z.object({
    branchId: branchIdSchema,
    shouldGenerate: z.boolean(),
  });
};
